import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingBag } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { noopFunction } from '@vestico/utils';

import { ClientReviewResponse, ProductWidget, isApparelMatchingCategory } from '@vestico/model';

import { Button, GlobalsContext, Modal, ModalSizing } from '@vestico/shared';

import { useAddToBagActions } from '../../hooks/useAddToBagActions';
import { AddToBagVariantSelect } from '../AddToBagVariantSelect';
import { WidgetRootElementContext } from '../MainWindow';
import { GenericOverlayIconProps, OverlayIcon } from './OverlayIcon';

export type AddToBagSize = 'default' | 'small';
export type AddToBagColorScheme = 'light' | 'dark';

export type AddToBagStyle = 'overlay' | 'button';
export interface AddToBagProps {
  product?: ProductWidget | null;
  response?: ClientReviewResponse | null;
  buttonStyle?: AddToBagStyle;
}

export const AddToBag = ({
  response,
  product,
  size,
  colorScheme,
  disableShadows,
  buttonStyle = 'overlay',
}: AddToBagProps & GenericOverlayIconProps) => {
  const rootElement = useContext(WidgetRootElementContext);
  const { useMobileLayout } = useContext(GlobalsContext);

  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const toggleModal = useCallback(() => setModalOpen(!modalOpen), [modalOpen]);

  const {
    addToBag,
    loadAvailableVariants,
    addToBagStatus,
    addToBagError,
    availableAddToBagVariants,
    addToBagEnabled,
    goToCartEnabled,
    goToCart,
    reloadPage,
  } = useAddToBagActions({
    response,
    product,
  });

  useEffect(() => {
    if (addToBagStatus !== 'addToBagSuccess') {
      return noopFunction();
    }

    const timeoutId = setTimeout(() => {
      setModalOpen(false);

      if (reloadPage && window) {
        window.location.reload();
      }
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [addToBagStatus, reloadPage]);

  useEffect(() => {
    if (addToBagStatus === 'loadingVariants') {
      return;
    }
    if (!availableAddToBagVariants || availableAddToBagVariants.length === 0) {
      setModalOpen(false);
    }
  }, [addToBagStatus, availableAddToBagVariants]);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    loadAvailableVariants();
  }, [loadAvailableVariants, modalOpen]);

  const modalTitle = useMemo(() => {
    if (addToBagStatus === 'addToBagSuccess') {
      return undefined;
    }

    if (isApparelMatchingCategory(product?.matchingCategory)) {
      return t('LOC_ADD_TO_BAG_HEADER_TITLE');
    }

    return t('LOC_ADD_TO_BAG_HEADER_TITLE_GENERIC');
  }, [addToBagStatus, product?.matchingCategory, t]);

  const addToBagModal = (
    <Modal
      open={modalOpen}
      sizing={ModalSizing.intrinsicHeight}
      alignment={useMobileLayout ? 'bottom' : 'center'}
      onClose={toggleModal}
      showClose
      header={modalTitle}
    >
      <AddToBagVariantSelect
        onAddToBag={addToBag}
        goToCartEnabled={goToCartEnabled}
        onGoToCart={goToCart}
        variants={availableAddToBagVariants}
        status={addToBagStatus}
        addToBagError={addToBagError}
        product={product}
      />
    </Modal>
  );

  const portal = createPortal(addToBagModal, rootElement ?? document.body);

  const title = useMemo(() => t('LOC_ADD_TO_BAG_BUTTON_TITLE'), [t]);

  if (!addToBagEnabled) {
    return null;
  }

  if (buttonStyle === 'button') {
    return (
      <div>
        <Button outline content={title} size="small" onClick={toggleModal} />
        {portal}
      </div>
    );
  }

  return (
    <>
      <OverlayIcon size={size} colorScheme={colorScheme} disableShadows={disableShadows} icon={<ShoppingBag />} text={title} onClick={toggleModal} />
      {portal}
    </>
  );
};
